/* Phase 10 UAT — shared scope + date + candidate filter bar.
   Used by Candidates / Interviews / Offers / DailyActivity / Evaluations pages so
   Team Leaders, Group Leads and Managers can pivot between their own work and
   their team's work without leaving the page.

   The "Specific Team Member" dropdown is populated from GET /directory/team-members
   which is already hierarchy-scoped server-side. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Funnel, ArrowClockwise } from "@phosphor-icons/react";

const IC_ROLES = ["executive", "sde", "senior_executive"];   // roles that always own records

export default function ScopeFilterBar({
  value = {}, onChange,
  showCandidate = true, candidates = [],
  showStatus = false, statusOptions = [], statusLabel = "Status",
  showDate = true,
  testid = "scope-filter-bar",
  extraLeft = null,
}) {
  const { user } = useAuth();
  const role = user?.role;
  const [members, setMembers] = useState([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const isSupervisor = ["admin", "super_admin", "team_lead", "sdl", "senior_executive"].includes(role);
  const isExecutiveOnly = IC_ROLES.includes(role) && role !== "senior_executive";

  useEffect(() => {
    if (!isSupervisor) { setMembersLoaded(true); return; }
    api.get("/directory/team-members")
      .then((r) => setMembers(r.data?.rows || []))
      .catch(() => setMembers([]))
      .finally(() => setMembersLoaded(true));
  }, [isSupervisor]);

  const patch = (k, v) => onChange({ ...value, [k]: v });

  // Executives don't get scope toggles — they only see "mine" implicitly.
  const scopeOptions = isSupervisor
    ? [
        ["mine", "My Records"],
        ["team", "My Team"],
        ...(members.length ? [["member", "Specific Team Member"]] : []),
        ["all", "All (visible)"],
      ]
    : [];

  const reset = () => onChange({});

  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-end gap-2"
         data-testid={testid}>
      <div className="flex items-center gap-1 text-xs uppercase tracking-widest text-slate-500 mr-1">
        <Funnel size={14} /> Filters
      </div>

      {extraLeft}

      {scopeOptions.length > 0 && (
        <div className="flex flex-col">
          <label className="text-[10px] uppercase text-slate-500">Scope</label>
          <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm min-w-[140px]"
                  data-testid={`${testid}-scope`}
                  value={value.scope || "all"}
                  onChange={(e) => {
                    const s = e.target.value;
                    const next = { ...value, scope: s };
                    if (s !== "member") delete next.member_id;
                    onChange(next);
                  }}>
            {scopeOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}

      {value.scope === "member" && members.length > 0 && (
        <div className="flex flex-col">
          <label className="text-[10px] uppercase text-slate-500">Team Member</label>
          <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm min-w-[180px]"
                  data-testid={`${testid}-member`}
                  value={value.member_id || ""}
                  onChange={(e) => patch("member_id", e.target.value)}>
            <option value="">— pick a member —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} · {m.team || "—"} · {m.role}
              </option>
            ))}
          </select>
        </div>
      )}

      {showCandidate && (
        <div className="flex flex-col">
          <label className="text-[10px] uppercase text-slate-500">Candidate</label>
          <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm min-w-[180px]"
                  data-testid={`${testid}-candidate`}
                  value={value.candidate_id || ""}
                  onChange={(e) => patch("candidate_id", e.target.value)}>
            <option value="">All candidates</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>{c.full_name}</option>
            ))}
          </select>
        </div>
      )}

      {showStatus && (
        <div className="flex flex-col">
          <label className="text-[10px] uppercase text-slate-500">{statusLabel}</label>
          <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm min-w-[140px]"
                  data-testid={`${testid}-status`}
                  value={value.status || ""}
                  onChange={(e) => patch("status", e.target.value)}>
            <option value="">All</option>
            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}

      {showDate && (
        <>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase text-slate-500">From</label>
            <input type="date" className="border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                   data-testid={`${testid}-date-from`}
                   value={value.date_from || ""}
                   onChange={(e) => patch("date_from", e.target.value)} />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase text-slate-500">To</label>
            <input type="date" className="border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                   data-testid={`${testid}-date-to`}
                   value={value.date_to || ""}
                   onChange={(e) => patch("date_to", e.target.value)} />
          </div>
        </>
      )}

      <button onClick={reset} type="button" data-testid={`${testid}-reset`}
              className="text-xs px-2 py-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1">
        <ArrowClockwise size={12} /> Reset
      </button>

      {isExecutiveOnly && (
        <div className="text-xs text-slate-400 ml-auto">Viewing your own records only.</div>
      )}
      {isSupervisor && !membersLoaded && (
        <div className="text-xs text-slate-400 ml-auto">Loading team…</div>
      )}
    </div>
  );
}

/* Serialise the filter state into a URLSearchParams-compatible object that
   matches the backend query params. Empty/undefined keys are dropped. */
export function scopeToQuery(v) {
  const p = {};
  if (v?.scope && v.scope !== "all") p.scope = v.scope;
  if (v?.member_id) p.member_id = v.member_id;
  if (v?.candidate_id) p.candidate_id = v.candidate_id;
  if (v?.status) p.status = v.status;
  if (v?.date_from) p.date_from = v.date_from;
  if (v?.date_to) p.date_to = v.date_to;
  return p;
}
